import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Search } from 'lucide-react';
import { Card, Button, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { subscriptionRequestAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

const statusMap = {
  pending: 'Pending Requests',
  approved: 'Approved Requests',
  rejected: 'Rejected Requests',
};

const SubscriptionRequestsPage = () => {
  const { status = 'pending' } = useParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    loadRequests();
  }, [page, status, typeFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await subscriptionRequestAPI.getAll(page, 20, status, search, typeFilter);
      setRequests(response.data.data.requests);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      toast.error('Failed to load subscription requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setPage(1);
    loadRequests();
  };

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') {
        await subscriptionRequestAPI.approve(id);
        toast.success('Request approved');
      } else {
        await subscriptionRequestAPI.reject(id);
        toast.success('Request rejected');
      }
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process request');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">{statusMap[status] || 'Requests'}</h1>
            <p className="text-gray-400 mt-2">Review subscription payments and update request status.</p>
          </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusMap).map(([key, label]) => (
            <Button
              key={key}
              variant={status === key ? 'primary' : 'secondary'}
              onClick={() => {
                setPage(1);
                setTypeFilter('');
                navigate(`/subscriptions/requests/${key}`);
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={typeFilter === '' ? 'primary' : 'secondary'}
            onClick={() => {
              setTypeFilter('');
              setPage(1);
            }}
          >
            All Types
          </Button>
          <Button
            variant={typeFilter === 'new' ? 'primary' : 'secondary'}
            onClick={() => {
              setTypeFilter('new');
              setPage(1);
            }}
          >
            New
          </Button>
          <Button
            variant={typeFilter === 'renew' ? 'primary' : 'secondary'}
            onClick={() => {
              setTypeFilter('renew');
              setPage(1);
            }}
          >
            Renew
          </Button>
        </div>
        <div className="flex flex-1 min-w-0 items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by device UUID or transaction ID"
            className="w-full rounded border border-gray-700 bg-slate-900 px-4 py-2 text-white placeholder:text-slate-500 focus:border-primary-500 focus:outline-none"
          />
          <Button type="submit">
            <Search size={16} />
            Search
          </Button>
        </div>
      </form>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading requests...</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No requests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3">Device UUID</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Transaction</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request._id} className="border-b border-gray-700 hover:bg-gray-800 transition">
                    <td className="px-4 py-4 text-white">{request.deviceUUID}</td>
                    <td className="px-4 py-4 text-gray-300">{request.packageId?.packageName || 'Unknown'}</td>
                    <td className="px-4 py-4 text-gray-300">{request.packageId?.price ?? '-'} TK</td>
                    <td className="px-4 py-4 text-gray-300">{request.paymentMethod}</td>
                    <td className="px-4 py-4 text-gray-300">{request.transactionId}</td>
                    <td className="px-4 py-4 text-gray-300">{new Date(request.submittedDate).toLocaleString()}</td>
                    <td className="px-4 py-4">
                      <Badge variant={request.requestType === 'renew' ? 'info' : 'default'}>
                        {request.requestType || 'new'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant={request.status === 'approved' ? 'success' : request.status === 'rejected' ? 'danger' : 'warning'}>
                        {request.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 space-x-2">
                      {request.status === 'pending' ? (
                        <>
                          <Button size="sm" variant="success" onClick={() => handleAction(request._id, 'approve')}>
                            <CheckCircle2 size={16} />
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => handleAction(request._id, 'reject')}>
                            <XCircle size={16} />
                          </Button>
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">No actions</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={(newPage) => setPage(newPage)} />
      </Card>
    </div>
  );
};

export default SubscriptionRequestsPage;
